import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import * as Styles from "../../layouts/PrivateLayout.styles";
import Button from 'components/ui/Button';
import {enqueueSnackbar} from 'notistack';
import ShopifySearch from 'components/ui/ShopifySearch/ShopifySearch';
import {useCreateDraftOrderMutation} from "../../services/orders";
import SelectBookingDetails from "../../components/ui/SelectBookingDetails";
import {format} from "date-fns";

const OrderCreatePage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [createDraftOrder, { isLoading }] = useCreateDraftOrderMutation();
    const [selectedVariant, setSelectedVariant] = useState<any>(null);
    const [bookingDetails, setBookingDetails] = useState<any>(null);
    const [showSelectBookingDetails, setShowSelectBookingDetails] = useState(false);
    const [isFeeProduct, setIsFeeProduct] = useState(false);
    const [originalBookingSlotId, setOriginalBookingSlotId] = useState<string | null>(null);
    const [originalClassId, setOriginalClassId] = useState<string | null>(null);

    useEffect(() => {
        const state = location.state as {
            insuranceFeeRequired?: boolean,
            orderId?: string,
            bookingId?: string,
            originalBookingSlotId?: string,
            originalClassId?: string
        };
        if (state?.insuranceFeeRequired) {
            setIsFeeProduct(true);
        }
        if (state?.originalBookingSlotId) {
            setOriginalBookingSlotId(state.originalBookingSlotId);
        }
        if (state?.originalClassId) {
            setOriginalClassId(state.originalClassId);
        }
    }, [location]);

    const handleLinkedProductChange = (selectedVariant: any) => {
        console.log(selectedVariant);
        setSelectedVariant(selectedVariant);
    };

    const handleSelectBookingDetails = () => {
        setShowSelectBookingDetails(true);
    };

    const handleBookingDetailsSelected = (details: any) => {
        setBookingDetails(details);
        setShowSelectBookingDetails(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedVariant) {
            enqueueSnackbar('Please select a product', { variant: 'error', autoHideDuration: 1000 });
            return;
        }
        if (!bookingDetails) {
            enqueueSnackbar('Please select booking details', { variant: 'error', autoHideDuration: 1000 });
            return;
        }
        try {
            const customAttributes = [
                { key: 'Date', value: bookingDetails.date },
                { key: 'Time', value: `${bookingDetails.startTime} - ${bookingDetails.endTime}` },
                { key: 'Location', value: bookingDetails.locationName },
                { key: '_sure_fire_location_id', value: bookingDetails.locationId }
            ];

            if (isFeeProduct && originalBookingSlotId && originalClassId) {
                customAttributes.push(
                    { key: '_original_booking_slot_id', value: originalBookingSlotId },
                    { key: '_original_class_id', value: originalClassId }
                );
            }

            const response = await createDraftOrder({
                lineItems: [{
                    variantId: selectedVariant.id,
                    quantity: 1,
                    customAttributes: customAttributes
                }],
            }).unwrap();

            if (response.draftOrder.id) {
                const state = location.state as { orderId?: string, bookingId?: string };
                enqueueSnackbar('Draft order created successfully', { variant: 'success', autoHideDuration: 1000 });
                window.open(`${process.env.REACT_APP_SHOPIFY_URL}/draft_orders/${response.draftOrder.id.toString().split("/").pop()}`, '_blank', 'noopener,noreferrer');
                navigate(`/admin/orders`);
                if (state?.orderId && state?.bookingId) {
                    navigate(`/admin/orders/${state.orderId}/booking-details/${state.bookingId}/edit`);
                } else {
                    window.open(`${process.env.REACT_APP_SHOPIFY_URL}/draft_orders/${response.draftOrder.id.toString().split("/").pop()}`, '_blank', 'noopener,noreferrer');
                }
            }
        } catch (error) {
            console.error('Error creating draft order:', error);
            enqueueSnackbar('Failed to create draft order', { variant: 'error', autoHideDuration: 1000 });
        }
    };
    if (showSelectBookingDetails) {
        return <SelectBookingDetails onDetailsSelected={handleBookingDetailsSelected} />;
    }

    const formatBookingDetails = (details: any) => {
        const date = new Date(details.date);
        const formattedDate = format(date, 'EEE, d MMM yyyy');
        const formattedTime = `${details.startTime} - ${details.endTime}`;
        return {
            date: formattedDate,
            time: formattedTime,
            location: details.locationName
        };
    };


    return (
        <div>
            <Styles.PageTitleContainer>
                <h1>{isFeeProduct ? 'Reschedule Fee Draft Order Creation' : 'Create Order'}</h1>
            </Styles.PageTitleContainer>
            <div style={{fontSize: 15}}>** Please select a product then assign the date and time. Once selected, you will be
                navigated back to Shopify to complete the draft order.
            </div>
            <form onSubmit={handleSubmit}>
                <Styles.FormColumns>
                    <Styles.FormColumnLeft>
                        <Styles.FormColumnLeftDescription>
                            <strong>Choose Product</strong>
                        </Styles.FormColumnLeftDescription>
                        <Styles.PageSubtitle>
                            {isFeeProduct
                                ? 'Insurance fee product is automatically selected.'
                                : 'Choose the Shopify product for this order.'}
                        </Styles.PageSubtitle>
                    </Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <ShopifySearch
                            id="product"
                            required
                            label="Product Name"
                            onChange={handleLinkedProductChange}
                            value={selectedVariant}
                            isFeeProduct={isFeeProduct}
                        />
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
                <Styles.FormColumns>
                    <Styles.FormColumnLeft>Booking Details</Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        {bookingDetails ? (
                            <div>
                                <Styles.FormColumnLeftDescription>
                                    <strong>Date:</strong>
                                </Styles.FormColumnLeftDescription>
                                <p>{formatBookingDetails(bookingDetails).date}</p>
                                <Styles.FormColumnLeftDescription>
                                    <strong>Time:</strong>
                                </Styles.FormColumnLeftDescription>
                                <p>{formatBookingDetails(bookingDetails).time}</p>
                                <Styles.FormColumnLeftDescription>
                                    <strong>Location:</strong>
                                </Styles.FormColumnLeftDescription>
                                <p>{formatBookingDetails(bookingDetails).location}</p>
                                <Button onClick={handleSelectBookingDetails}>Change</Button>
                            </div>
                        ) : (
                            <Button onClick={handleSelectBookingDetails}>Select Registration Details</Button>
                        )}
                    </Styles.FormColumnRight>
                </Styles.FormColumns>

                <Styles.FormColumns>
                    <Styles.FormColumnLeft></Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <Button disabled={!bookingDetails || !selectedVariant} type="submit" loading={isLoading} cc-color="purple">Create Draft Order</Button>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
            </form>
        </div>
    );
};

export default OrderCreatePage;
